import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileQuestion, SearchX } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout, ConfirmModal, EmptyState, FilePreview,
  IconButton, LinkedText, PageHeader, StatusBadge, UserAvatar,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import AssignmentFacts from "./AssignmentFacts";
import GradePanel from "./GradePanel";
import { assignmentsBase, formatDue, gradingOrder, nextToGrade, reviewRows } from "./assignmentRules";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * SubmissionReviewPage — `/batch-assignments/:id/review/:submissionId` (and
 * `/i/…`): one student's work beside the grade, and a way through the queue.
 *
 * Every grade is sent with the `submittedAt` this page loaded. If the student
 * replaced their work meanwhile the server refuses it (409) and the page says
 * so, keeping what the grader typed — a grade never lands on work nobody read.
 */
const SubmissionReviewPage = () => {
  const { id, submissionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const base = assignmentsBase(getCurrentUser()?.usertype);
  const listPath = location.state?.from || `${base}/${id}`;

  const [state, setState] = useState({ loading: true, error: null, submission: null, assignment: null, deadline: null, dateOnly: false });
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const notify = (message, type = "success") => setToast({ show: true, message, type });

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    setConflict("");
    apiClient.get(`/general-assignments/submissions/${submissionId}`)
      .then(({ data }) => setState({
        loading: false, error: null, submission: data.submission, assignment: data.assignment,
        deadline: data.deadline, dateOnly: !!data.dateOnly,
      }))
      .catch((error) => setState({ loading: false, error, submission: null, assignment: null, deadline: null, dateOnly: false }));
  }, [submissionId]);

  const loadQueue = useCallback(() => {
    apiClient.get(`/general-assignments/${id}/submissions`)
      .then(({ data }) => setQueue(reviewRows(data.submissions || [], [])))
      .catch(() => setQueue([]));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const order = useMemo(() => gradingOrder(queue), [queue]);
  const position = order.indexOf(submissionId);
  const prevId = position > 0 ? order[position - 1] : null;
  const nextId = position >= 0 && position < order.length - 1 ? order[position + 1] : null;
  const nextUngraded = useMemo(() => nextToGrade(queue, submissionId), [queue, submissionId]);

  const pathFor = (sid) => `${base}/${id}/review/${sid}`;
  // Moving between submissions REPLACES the history entry, so Back from any of
  // them returns to the list the grader came from rather than walking the queue.
  const leaveTo = (path) => {
    if (path === listPath && location.state?.from && window.history.state?.idx > 0) navigate(-1);
    else navigate(path, { replace: path !== listPath, state: { from: listPath } });
  };
  const go = (path) => {
    if (dirty && !busy) {
      setPendingPath(path);
      return;
    }
    leaveTo(path);
  };

  const header = (title, description, actions) => (
    <PageHeader title={title} description={description} backButton onBack={() => go(listPath)} actions={actions} />
  );

  if (state.loading && !state.submission) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading submission">
        {header("Review submission")}
        <div className="tw-grid tw-gap-4 min-[992px]:tw-grid-cols-3">
          <div className="min-[992px]:tw-col-span-2"><Shimmer height={360} /></div>
          <Shimmer height={360} />
        </div>
      </div>
    );
  }

  const { submission, assignment } = state;
  if (!submission) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header("Review submission")}
        {state.error?.response?.status === 404 ? (
          <EmptyState
            icon={SearchX}
            title="Submission not found"
            description="It may have been removed with its assignment."
            action={<AppButton variant="outline" onClick={() => navigate(listPath)}>Back to the assignment</AppButton>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            Could not load this submission.
          </Callout>
        )}
      </div>
    );
  }

  const student = submission.studentId || {};
  const maxMarks = assignment?.maxMarks;

  /** Shared by grading and sending back: the server's answer replaces the page's copy. */
  const save = async (url, body, success) => {
    setBusy(true);
    setConflict("");
    try {
      const { data } = await apiClient.put(url, { ...body, expectedSubmittedAt: submission.submittedAt });
      setState((s) => ({ ...s, submission: { ...data.submission, studentId: data.submission.studentId || s.submission.studentId } }));
      setQueue((rows) => rows.map((r) => (r.submissionId === submissionId
        ? { ...r, status: data.submission.status, marks: data.submission.marks }
        : r)));
      setDirty(false);
      notify(success);
      return data.submission;
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "SUBMISSION_CHANGED") setConflict(err.response.data.message);
      else if (!err?.response?.data?.errors) notify(err?.response?.data?.message || "That was not saved. Try again.", "error");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const onGrade = async ({ marks, feedback, next }) => {
    await save(`/general-assignments/submissions/${submissionId}/grade`, { marks, feedback }, "Grade saved.");
    if (next && nextUngraded) navigate(pathFor(nextUngraded), { replace: true, state: { from: listPath } });
  };

  const onReturn = ({ feedback }) => save(
    `/general-assignments/submissions/${submissionId}/request-resubmit`,
    { feedback },
    "Sent back. The student has been told what to change.",
  );

  const navActions = order.length > 1 && position >= 0 ? (
    <div className="tw-flex tw-items-center tw-gap-1">
      <IconButton label="Previous submission" icon={ChevronLeft} variant="outline" size="sm" disabled={!prevId} onClick={() => go(pathFor(prevId))} />
      <span className="tw-px-1 tw-text-[0.8125rem] tw-tabular-nums tw-text-content-muted">{position + 1} of {order.length}</span>
      <IconButton label="Next submission" icon={ChevronRight} variant="outline" size="sm" disabled={!nextId} onClick={() => go(pathFor(nextId))} />
    </div>
  ) : null;

  const text = String(submission.textResponse || "");

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {header(assignment?.title || "Review submission", assignment?.batchId?.name || "", navActions)}

      {conflict && (
        <Callout tone="warning" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Reload</AppButton>}>
          {conflict}
        </Callout>
      )}

      <div className="tw-grid tw-items-start tw-gap-4 min-[992px]:tw-grid-cols-3">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4 min-[992px]:tw-col-span-2">
          <AppCard>
            <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                  <UserAvatar name={student.name || "?"} size="md" />
                  <div className="tw-min-w-0">
                    <p className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-content">{student.name || "Unknown student"}</p>
                    {student.email && <p className="tw-m-0 tw-truncate tw-text-xs tw-text-content-subtle">{student.email}</p>}
                  </div>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                  <StatusBadge status={submission.status} domain="assignmentReview" />
                  {submission.late && <StatusBadge status="warning">Late</StatusBadge>}
                  {submission.attempt > 1 && <StatusBadge status="neutral">{ordinal(submission.attempt)} attempt</StatusBadge>}
                </div>
              </div>
              <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                Handed in {formatDue(submission.submittedAt)}
              </p>

              {submission.attempt > 1 && submission.lastReturn?.note && submission.status !== "resubmit_requested" && (
                <Callout tone="info">
                  <span className="tw-font-semibold">
                    {submission.lastReturn.by?.name ? `${submission.lastReturn.by.name} asked` : "You asked"} for changes:
                  </span>{" "}
                  {submission.lastReturn.note}
                </Callout>
              )}

              <div>
                <h2 className="tw-mb-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">Written answer</h2>
                {text.trim() ? (
                  <div className="tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3">
                    <LinkedText text={text} />
                  </div>
                ) : (
                  <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-subtle">No written answer — the work is in the files.</p>
                )}
              </div>

              <div>
                <h2 className="tw-mb-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">
                  Files {submission.attachments?.length ? `(${submission.attachments.length})` : ""}
                </h2>
                {submission.attachments?.length ? (
                  <>
                    <FilePreview attachments={submission.attachments} />
                    <p className="tw-m-0 tw-mt-1.5 tw-text-xs tw-text-content-subtle">
                      Click a file to read it here — spreadsheets and CSV files open as tables.
                    </p>
                  </>
                ) : (
                  <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-subtle">No files attached.</p>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          {assignment && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                    <FileQuestion size={16} aria-hidden="true" /> The assignment
                  </span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
                <AssignmentFacts
                  deadline={state.deadline || assignment.dueDate}
                  dateOnly={state.dateOnly}
                  maxMarks={assignment.maxMarks}
                  lateAllowed={assignment.allowLateSubmissions === true}
                  closed={assignment.status === "closed"}
                />
                {assignment.description && (
                  <details className="tw-group">
                    <summary className="tw-cursor-pointer tw-text-[0.8125rem] tw-font-medium tw-text-primary">Instructions</summary>
                    <LinkedText text={assignment.description} className="tw-mt-2" />
                  </details>
                )}
                {assignment.attachments?.length > 0 && <FilePreview attachments={assignment.attachments} compact />}
              </AppCardContent>
            </AppCard>
          )}
        </div>

        <div className="min-[992px]:tw-sticky min-[992px]:tw-top-4">
          <GradePanel
            submission={submission}
            maxMarks={maxMarks}
            onGrade={onGrade}
            onReturn={onReturn}
            hasNext={Boolean(nextUngraded)}
            onDirtyChange={setDirty}
            busy={busy}
          />
        </div>
      </div>

      <ConfirmModal
        show={Boolean(pendingPath)}
        title="Leave without saving?"
        message="The marks and feedback you typed for this student have not been saved."
        confirmText="Leave"
        variant="warning"
        onConfirm={() => {
          const path = pendingPath;
          setPendingPath(null);
          setDirty(false);
          leaveTo(path);
        }}
        onHide={() => setPendingPath(null)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default SubmissionReviewPage;
